import { ChangeEvent, useMemo, useState } from 'react';
import { useCountriesQuery } from '@access-cms/api-client';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { required } from '../../utils/fieldValidation';
import { STATE_AND_TERRITORY_OPTIONS } from '../../../../utils/states';

export interface BirthInformationFormData {
  sex: string;
  birthCountry: string;
  birthCountryCode: string;
  birthCity: string;
  birthState: string;
}

const sexOptions = [
  { label: '- Select an option -', value: '' },
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
];

function BirthInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<BirthInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'birth-information-page';
  const countriesQuery = useCountriesQuery({ excludeUs: false });

  const countries = useMemo(() => countriesQuery.data?.data ?? [], [countriesQuery.data?.data]);
  const countryOptions = useMemo(() => {
    const options = countries
      .filter((country) => country.countryCode && country.countryName)
      .map((country) => ({
        label: country.countryName as string,
        value: country.countryCode as string,
      }));
    const usOption = options.find((option) => option.value === 'US');

    return [
      { label: '- Start typing to filter results -', value: '' },
      ...(usOption ? [usOption] : []),
      ...options.filter((option) => option.value !== 'US'),
    ];
  }, [countries]);
  const selectedBirthCountryCode = useMemo(() => {
    if (data.birthCountryCode) return data.birthCountryCode;

    return countries.find((country) => country.countryName === data.birthCountry)?.countryCode ?? '';
  }, [countries, data.birthCountry, data.birthCountryCode]);
  const isBirthCountryUS = selectedBirthCountryCode === 'US';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'sex', label: 'Sex', validationRules: [required] },
      { field: 'birthCountry', label: 'Birth Country', validationRules: [required] },
      { field: 'birthCity', label: 'Birth City', validationRules: [required] },
      { field: 'birthState', label: 'Birth State/Province', validationRules: [required] },
    ],
  });

  const handleChange = (field: keyof BirthInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = countries.find((country) => country.countryCode === countryCode);
    const birthCountry = selectedCountry?.countryName ?? '';

    updateData({
      ...data,
      birthCountry,
      birthCountryCode: countryCode,
    });
    handleValidationChange('birthCountry', birthCountry);
  };

  const birthCountryError =
    getFieldError('birthCountry') ||
    (countriesQuery.isError ? 'Unable to load countries. Please refresh and try again.' : undefined);

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-sex`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-sex-dropdown`}
          errorMessage={getFieldError('sex')}
          hint='Select your sex as it appears on your birth certificate'
          label='Sex'
          name='sex'
          options={sexOptions}
          value={data.sex || ''}
          onBlur={() => handleBlur('sex')}
          onChange={(value: string) => handleChange('sex', value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-country`}>
        <Dropdown
          required
          searchable
          data-testid={`${baseTestId}-country-dropdown`}
          disabled={countriesQuery.isLoading || countriesQuery.isError}
          errorMessage={birthCountryError}
          hint='Start typing to filter country results'
          label='Birth Country'
          name='birthCountry'
          options={countryOptions}
          tooltipText='The country where you were born, as shown on your birth certificate or official identity documents.'
          value={selectedBirthCountryCode || ''}
          onBlur={() => handleBlur('birthCountry')}
          onChange={handleCountryChange}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-city`}>
        <TextField
          required
          data-testid={`${baseTestId}-city-field`}
          errorMessage={getFieldError('birthCity')}
          hint='Enter the city where you were born'
          label='Birth City'
          name='birthCity'
          tooltipText='The city or town where you were born, as shown on your birth certificate.'
          value={data.birthCity || ''}
          onBlur={() => handleBlur('birthCity')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('birthCity', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-state`}>
        {isBirthCountryUS ? (
          <Dropdown
            required
            searchable
            data-testid={`${baseTestId}-state-dropdown`}
            errorMessage={getFieldError('birthState')}
            hint='Select your birth state or territory'
            label='Birth State/Territory'
            name='birthState'
            options={STATE_AND_TERRITORY_OPTIONS}
            value={data.birthState || ''}
            onBlur={() => handleBlur('birthState')}
            onChange={(value: string) => handleChange('birthState', value)}
          />
        ) : (
          <TextField
            required
            data-testid={`${baseTestId}-state-field`}
            errorMessage={getFieldError('birthState')}
            hint='Enter the state or province where you were born'
            label='Birth State/Province'
            name='birthState'
            tooltipText='The state, province, or region where you were born. For U.S. births, enter the state name. For international births, enter the province or region.'
            value={data.birthState || ''}
            onBlur={() => handleBlur('birthState')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('birthState', e.target.value)}
          />
        )}
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Birth Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Birth Details</h3>
          <p>
            Please share your birth details just as they appear on your birth certificate or other official documents.
          </p>

          <h4 className='ds-u-margin-top--3'>Sex</h4>
          <p>Select your sex as it appears on your birth certificate.</p>

          <h4 className='ds-u-margin-top--3'>Birth Country</h4>
          <p>
            Select the country where you were born. Start typing to filter the list of countries. If your birth country
            is not listed, please contact the CMS Service Desk by phone at 1-800-562-1963 or 410-786-2580, or email{' '}
            <a href='mailto:CMS_IT_Service_Desk@cms.hhs.gov'>CMS_IT_Service_Desk@cms.hhs.gov</a>.
          </p>

          <h4 className='ds-u-margin-top--3'>Birth City</h4>
          <p>Enter the city or town where you were born.</p>

          <h4 className='ds-u-margin-top--3'>Birth State/Province</h4>
          <p>
            Enter the state or province where you were born. This field is required. If you were born in the United
            States, enter the state name. If you were born outside the U.S., enter the province or region name.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default BirthInformationPage;
